/**
 * PDP floating video card + player (sections/pdp-video.liquid).
 *
 * Native pieces do the heavy lifting: <dialog> gives the top layer, focus trap and
 * Esc-to-close; CSS scroll-snap gives the swipe (vertical on mobile, horizontal on
 * desktop); an IntersectionObserver watching a thin strip through the middle of the
 * viewport decides which slide is active.
 */
if (!customElements.get('pdp-video')) {
  const DESKTOP = window.matchMedia('(min-width: 768px)');
  const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');
  const DRAG_THRESHOLD = 6;
  const EDGE_GAP = 16;

  class PdpVideo extends HTMLElement {
    connectedCallback() {
      this.card = this.querySelector('[data-pv-card]');
      this.cardVideo = this.querySelector('.pdp-video__card-video');
      this.dialog = this.querySelector('[data-pv-dialog]');
      this.viewport = this.querySelector('[data-pv-viewport]');
      this.slides = [...this.querySelectorAll('[data-pv-slide]')];
      this.videos = this.slides.map(slide => slide.querySelector('video'));
      this.progress = this.querySelector('[data-pv-progress]');
      this.muteButton = this.querySelector('[data-pv-mute]');
      this.prevButton = this.querySelector('[data-pv-prev]');
      this.nextButton = this.querySelector('[data-pv-next]');
      this.toast = this.querySelector('[data-pv-toast]');

      this.activeIndex = -1;
      this.isMuted = false;
      this.isDesignMode = Boolean(window.Shopify?.designMode);
      this.storageKey = `pdp-video-closed:${this.dataset.productId}`;
      this.abort = new AbortController();

      if (!this.card || !this.dialog || this.wasClosed()) return;

      const on = (target, type, handler, options = {}) =>
        target?.addEventListener(type, handler, { ...options, signal: this.abort.signal });

      on(this.querySelector('[data-pv-card-close]'), 'click', this.closeCard);
      on(this.querySelector('[data-pv-open]'), 'click', this.handleOpenClick);
      this.querySelectorAll('[data-pv-dismiss]').forEach(button => on(button, 'click', () => this.dialog.close()));
      on(this.dialog, 'close', this.handlePlayerClose);
      on(this.dialog, 'keydown', this.handleKeydown);
      on(this.viewport, 'click', this.handleViewportClick);
      on(this.muteButton, 'click', this.toggleMute);
      on(this.querySelector('[data-pv-share]'), 'click', this.share);
      on(this.prevButton, 'click', () => this.goTo(this.activeIndex - 1));
      on(this.nextButton, 'click', () => this.goTo(this.activeIndex + 1));
      on(DESKTOP, 'change', () => this.dialog.open && this.observeSlides());

      if (this.hasAttribute('data-draggable')) this.enableDrag(on);

      const show = () => setTimeout(this.showCard, (Number(this.dataset.delay) || 0) * 1000);
      if (document.readyState === 'complete') show();
      else on(window, 'load', show, { once: true });
    }

    disconnectedCallback() {
      this.abort?.abort();
      this.observer?.disconnect();
      cancelAnimationFrame(this.frame);
      document.documentElement.classList.remove('pdp-video-lock');
    }

    /* ── Floating card ──────────────────────────────────────────────────── */

    showCard = () => {
      if (!this.isConnected) return;
      this.card.hidden = false;
      requestAnimationFrame(() => this.card.classList.add('is-visible'));
      this.playCardVideo();
    };

    playCardVideo() {
      if (REDUCED_MOTION.matches) return;
      this.cardVideo.play().catch(() => {});
    }

    closeCard = () => {
      this.cardVideo.pause();
      this.card.hidden = true;
      if (this.isDesignMode) return;
      try {
        sessionStorage.setItem(this.storageKey, '1');
      } catch {}
    };

    wasClosed() {
      if (this.isDesignMode) return false;
      try {
        return sessionStorage.getItem(this.storageKey) === '1';
      } catch {
        return false;
      }
    }

    handleOpenClick = () => {
      if (this.suppressClick) {
        this.suppressClick = false;
        return;
      }
      this.openPlayer();
    };

    enableDrag(on) {
      let drag = null;

      on(this.card, 'pointerdown', event => {
        this.suppressClick = false;
        if (event.button !== 0 || event.target.closest('[data-pv-card-close]')) return;
        const rect = this.card.getBoundingClientRect();
        drag = { id: event.pointerId, x: event.clientX, y: event.clientY, left: rect.left, top: rect.top, moved: false };
      });

      on(this.card, 'pointermove', event => {
        if (!drag || event.pointerId !== drag.id) return;
        const dx = event.clientX - drag.x;
        const dy = event.clientY - drag.y;

        if (!drag.moved) {
          if (Math.hypot(dx, dy) < DRAG_THRESHOLD) return;
          drag.moved = true;
          this.card.setPointerCapture(event.pointerId);
          this.card.classList.add('is-dragging');
        }

        this.placeCard(drag.left + dx, drag.top + dy);
      });

      const end = () => {
        if (drag?.moved) {
          this.suppressClick = true;
          this.card.classList.remove('is-dragging');
        }
        drag = null;
      };
      on(this.card, 'pointerup', end);
      on(this.card, 'pointercancel', end);

      on(window, 'resize', () => {
        if (!this.wasDragged) return;
        const rect = this.card.getBoundingClientRect();
        this.placeCard(rect.left, rect.top);
      });
    }

    placeCard(left, top) {
      const { width, height } = this.card.getBoundingClientRect();
      const x = Math.min(Math.max(EDGE_GAP, left), window.innerWidth - width - EDGE_GAP);
      const y = Math.min(Math.max(EDGE_GAP, top), window.innerHeight - height - EDGE_GAP);
      Object.assign(this.card.style, { left: `${x}px`, top: `${y}px`, right: 'auto', bottom: 'auto' });
      this.wasDragged = true;
    }

    /* ── Player ─────────────────────────────────────────────────────────── */

    openPlayer() {
      this.cardVideo.pause();
      this.classList.add('is-player-open');
      document.documentElement.classList.add('pdp-video-lock');

      /* Opened by a tap, so the browser allows sound — that's the "Tap to unmute" promise. */
      this.isMuted = false;
      this.activeIndex = -1;

      this.dialog.showModal();
      this.scrollToSlide(0, 'instant');
      this.observeSlides();
      this.activate(0);
      this.tick();
    }

    handlePlayerClose = () => {
      this.observer?.disconnect();
      cancelAnimationFrame(this.frame);
      this.videos.forEach(video => video.pause());
      this.classList.remove('is-player-open');
      document.documentElement.classList.remove('pdp-video-lock');
      if (!this.card.hidden) this.playCardVideo();
    };

    observeSlides() {
      this.observer?.disconnect();
      const strip = DESKTOP.matches ? '0px -49% 0px -49%' : '-49% 0px -49% 0px';
      this.observer = new IntersectionObserver(
        entries => {
          const hit = entries.find(entry => entry.isIntersecting);
          if (hit) this.activate(this.slides.indexOf(hit.target));
        },
        { root: this.viewport, rootMargin: strip, threshold: 0 }
      );
      this.slides.forEach(slide => this.observer.observe(slide));
    }

    activate(index) {
      if (index === this.activeIndex || !this.videos[index]) return;
      this.activeIndex = index;

      this.slides.forEach((slide, i) => slide.classList.toggle('is-active', i === index));
      this.videos.forEach((video, i) => i !== index && video.pause());

      const video = this.videos[index];
      if (!video.src) video.src = video.dataset.src;
      video.currentTime = 0;
      video.muted = this.isMuted;
      video.play().catch(() => {
        /* Sound refused (no user gesture): fall back to muted playback. */
        this.isMuted = true;
        video.muted = true;
        this.syncMute();
        video.play().catch(() => {});
      });

      this.syncMute();
      if (this.prevButton) this.prevButton.disabled = index === 0;
      if (this.nextButton) this.nextButton.disabled = index === this.slides.length - 1;
    }

    goTo(index) {
      if (index < 0 || index >= this.slides.length) return;
      this.scrollToSlide(index, REDUCED_MOTION.matches ? 'instant' : 'smooth');
    }

    /*
     * Scroll the viewport (not the slide via scrollIntoView, which could scroll the
     * page behind the dialog). Measured from rendered rects rather than offsetLeft/Top,
     * because the desktop coverflow's overlapping negative margins make offset math
     * land on the wrong slide.
     */
    scrollToSlide(index, behavior) {
      const slide = this.slides[index];
      if (!slide) return;

      const viewportRect = this.viewport.getBoundingClientRect();
      const slideRect = slide.getBoundingClientRect();

      if (DESKTOP.matches) {
        const delta = slideRect.left + slideRect.width / 2 - (viewportRect.left + viewportRect.width / 2);
        this.viewport.scrollTo({ left: this.viewport.scrollLeft + delta, behavior });
      } else {
        const delta = slideRect.top - viewportRect.top;
        this.viewport.scrollTo({ top: this.viewport.scrollTop + delta, behavior });
      }
    }

    handleViewportClick = event => {
      const slide = event.target.closest('[data-pv-slide]');

      /* Click on the empty space beside the videos (desktop) closes, like a lightbox. */
      if (!slide) {
        if (event.target === this.viewport) this.dialog.close();
        return;
      }

      const index = this.slides.indexOf(slide);
      if (index !== this.activeIndex) {
        this.goTo(index);
        return;
      }

      const video = this.videos[index];
      video.paused ? video.play().catch(() => {}) : video.pause();
    };

    handleKeydown = event => {
      const next = event.key === 'ArrowRight' || event.key === 'ArrowDown';
      const prev = event.key === 'ArrowLeft' || event.key === 'ArrowUp';
      if (!next && !prev) return;
      event.preventDefault();
      this.goTo(this.activeIndex + (next ? 1 : -1));
    };

    toggleMute = () => {
      this.isMuted = !this.isMuted;
      const video = this.videos[this.activeIndex];
      if (video) video.muted = this.isMuted;
      this.syncMute();
    };

    syncMute() {
      if (!this.muteButton) return;
      this.muteButton.setAttribute('aria-pressed', String(this.isMuted));
      this.muteButton.setAttribute('aria-label', this.isMuted ? 'Unmute' : 'Mute');
    }

    tick = () => {
      const video = this.videos[this.activeIndex];
      if (video && this.progress && video.duration) {
        this.progress.style.transform = `scaleX(${video.currentTime / video.duration})`;
      }
      this.frame = requestAnimationFrame(this.tick);
    };

    share = async () => {
      const data = { title: this.dataset.title, url: this.dataset.url };

      if (navigator.share) {
        try {
          await navigator.share(data);
        } catch {}
        return;
      }

      try {
        await navigator.clipboard.writeText(data.url);
      } catch {
        return;
      }

      if (!this.toast) return;
      this.toast.hidden = false;
      clearTimeout(this.toastTimer);
      this.toastTimer = setTimeout(() => (this.toast.hidden = true), 2000);
    };
  }

  customElements.define('pdp-video', PdpVideo);
}
